import { clsx } from 'clsx';
import { Select } from 'antd';
import { useListContext } from 'ra-core';
import { useModalContext } from '@providers';
import React, { useEffect, useState } from 'react';
import { useNotify, useResourceContext } from 'react-admin';

import { useRequest } from '@hooks';
import { financeSettingsHttp } from '@network/finance-settings-http';
import { DealerUniqueCropsField, UniqueCropProps } from './DealerUniqueCropsField';
import { BlueButton, BlueOutlineButton, InputSelectAutocomplete } from '@components';
import { AdminOptionCats, Crop, dealerTiers, DealerType, dealerVerqorRR } from '@types';
import { adminHttp, adminSettingsHttp, cropHttp, dealerHttp, stateHttp, UpdateBulkDealerParams } from '@network';
import cls from './DealerListActions.module.css';

const propsList = [
  'Owner', 'Status', 'States', 'Type', 'Activation', 'Channel', 'Risk Grade', 'Verqor RR', 'Unique Crops',
];
type PropType = 'Owner' | 'Status' | 'States' | 'Type' |
  'Activation' | 'Channel' | 'Risk Grade' | 'Verqor RR' | 'Unique Crops';
type Option = {value: number | string; label: string};
const typesCategory = {
  [DealerType.BUYER]: AdminOptionCats.PARTNERS_TYPE_BUYER,
  [DealerType.RETAILER]: AdminOptionCats.PARTNERS_TYPE_RETAILER,
  [DealerType.ASSOCIATE]: AdminOptionCats.PARTNERS_TYPE_ASSOCIATE,
} as const;

export const DealerListActions = () => {
  const { selectedIds, refetch } = useListContext();
  const [prop, setProp] = useState<PropType>();
  const { hideModal } = useModalContext();
  const notify = useNotify();
  const resource = useResourceContext();
  const dealerType: DealerType = resource === 'dealer' ?
    DealerType.BUYER : resource === 'dealer-retailer' ?
      DealerType.RETAILER : DealerType.ASSOCIATE;

  const [ownersOptions, setOwnersOptions] = useState<Option[]>([]);
  const [ownerId, setOwnerId] = useState<number>();
  const [statusOptions, setStatusOptions] = useState<Option[]>([]);
  const [statusId, setStatusId] = useState<number>();
  const [statesOptions, setStatesOptions] = useState<Option[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const [priorityOptions, setPriorityOptions] = useState<Option[]>([]);
  const [priorityId, setPriorityId] = useState<number>();
  const [activationOptions, setActivationOptions] = useState<Option[]>([]);
  const [activationId, setActivationId] = useState<number>();
  const [sourceOptions, setSourceOptions] = useState<Option[]>([]);
  const [source, setSource] = useState<string>();
  const [tier, setTier] = useState<string>();
  const [verqorRR, setVerqorRR] = useState<string>();
  const [crops, setCrops] = useState<Crop[]>([]);
  const [uniqueCrops, setUniqueCrops] = useState<UniqueCropProps[]>([]);

  const resetValues = () => {
    setOwnerId(undefined);
  };

  const onCancel = () => {
    setProp(undefined);
    resetValues();
    hideModal();
  };

  const { loading, submit } = useRequest(async () => {
    if (!Array.isArray(selectedIds) || selectedIds.length === 0) {
      return notify('No Users are selected');
    }
    if (prop === 'Owner' && !ownerId) {
      return notify('No owner is selected');
    }
    if (prop === 'Status' && !statusId) {
      return notify('No status is selected');
    }
    if (prop === 'States' && !states.length) {
      return notify('No states selected');
    }
    if (prop === 'Type' && !priorityId) {
      return notify('No type is selected');
    }
    if (prop === 'Activation' && !activationId) {
      return notify('No activation is selected');
    }
    if (prop === 'Channel' && !source) {
      return notify('No channel is selected');
    }
    if (prop === 'Risk Grade' && !tier) {
      return notify('No Risk Grade is selected');
    }
    if (prop === 'Verqor RR' && !verqorRR) {
      return notify('No Verqor RR is selected');
    }
    if (prop === 'Unique Crops' && !uniqueCrops?.length) {
      return notify('No Unique Crops are selected');
    }

    const payload: UpdateBulkDealerParams = {
      dealerIds: selectedIds,
      ownerId,
      statusId,
      priorityId,
      activationId,
      source,
      tier,
      verqorRR,
      uniqueCrops,
    };
    if (states.length) {
      payload.states = states;
    }

    const updatedTotal = await dealerHttp.updateBulk(payload);

    notify(`Updated ${updatedTotal} users`);
    onCancel();
    refetch();
  });

  useEffect(() => void resetValues(), [prop]);

  useEffect(() => {
    // load options
    (async () => {
      const [
        owners,
        statuses,
        allStates,
        types,
        allActivations,
        allSources,
        allCrops,
      ] = await Promise.all([
        adminHttp.getAll(),
        adminSettingsHttp.getOptionsByCategory(AdminOptionCats.PARTNERS_STATUS),
        stateHttp.getAll(),
        adminSettingsHttp.getOptionsByCategory(typesCategory[dealerType]),
        financeSettingsHttp.getActivations(),
        dealerHttp.getSourceOptions(),
        cropHttp.getAll(),
      ]);

      const sortedOwners: Option[] = owners.map(admin => ({
        value: admin.id,
        label: admin.name,
      })).sort((a, b) => a.label > b.label ? 1 : -1);
      const sortedStatuses: Option[] = statuses.map(i => ({
        value: i.id as number,
        label: i.name,
      })).sort((a, b) => a.label > b.label ? 1 : -1);
      const sortedStates: Option[] = allStates.map(i => ({
        value: i.name,
        label: i.name,
      })).sort((a, b) => a.label > b.label ? 1 : -1);

      setStatusOptions(sortedStatuses);
      setOwnersOptions(sortedOwners);
      setStatesOptions(sortedStates);
      setPriorityOptions(types.map(i => ({
        value: i.id as number,
        label: i.name,
      })));
      setActivationOptions(allActivations.map(activation => ({
        value: activation.id,
        label: activation.activation,
      })));
      setSourceOptions(allSources);
      setCrops(allCrops);
    })();
  }, []);

  useEffect(() => {
    console.log(uniqueCrops);
  }, [uniqueCrops]);

  return (
    <div className={cls.box}>
      <div className={cls.title}>Properties to update</div>
      <Select value={prop} onChange={v => setProp(v)} className={cls.select}
        placeholder="Select a property to edit">
        {propsList.map(propItem => (
          <Select.Option key={propItem} value={propItem}>
            {propItem}
          </Select.Option>
        ))}
      </Select>

      <div className={cls.content}>
        {prop && <div className={cls.title}>{prop}</div>}
        {prop === 'Owner' && (
          <Select
            value={ownerId}
            onChange={v => setOwnerId(+v)}
            className={cls.select}
            placeholder="Select owner"
            showSearch
            optionFilterProp="children"
            status={!ownerId ? 'error' : undefined}
          >
            {ownersOptions.map(option => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        )}
        {prop === 'Status' && (
          <Select
            value={ownerId}
            onChange={v => setStatusId(+v)}
            className={cls.select}
            placeholder="Select status"
            showSearch
            optionFilterProp="children"
            status={!statusId ? 'error' : undefined}
          >
            {statusOptions.map(option => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        )}
        {prop === 'States' && (
          <Select
            mode="tags"
            size="small"
            placeholder="Select states"
            defaultValue={states}
            onChange={setStates}
            className={clsx(cls.select, cls.tagsSelect)}
            options={statesOptions}
          />
        )}
        {prop === 'Type' && (
          <Select
            value={priorityId}
            onChange={v => setPriorityId(+v)}
            className={cls.select}
            placeholder="Select type"
            showSearch
            optionFilterProp="children"
            status={!priorityId ? 'error' : undefined}
          >
            {priorityOptions.map(option => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        )}
        {prop === 'Activation' && (
          <InputSelectAutocomplete
            wordWrap
            label=""
            onChange={setActivationId}
            options={activationOptions}
            selectedValue={activationId || ''}
            displayEmpty
          />
        )}
        {prop === 'Channel' && (
          <Select
            value={source}
            onChange={setSource}
            className={cls.select}
            placeholder="Select channel"
            showSearch
            optionFilterProp="children"
            status={!source ? 'error' : undefined}
          >
            {sourceOptions.map(option => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        )}
        {prop === 'Risk Grade' && (
          <Select
            value={tier}
            onChange={setTier}
            className={cls.select}
            placeholder="Select channel"
            showSearch
            optionFilterProp="children"
            status={!tier ? 'error' : undefined}
          >
            {dealerTiers.map(option => (
              <Select.Option key={option} value={option}>{option}</Select.Option>
            ))}
          </Select>
        )}
        {prop === 'Verqor RR' && (
          <Select
            value={verqorRR}
            onChange={setVerqorRR}
            className={cls.select}
            placeholder="Select channel"
            showSearch
            optionFilterProp="children"
            status={!verqorRR ? 'error' : undefined}
          >
            {dealerVerqorRR[dealerType].map(option => (
              <Select.Option key={option} value={option}>{option}</Select.Option>
            ))}
          </Select>
        )}
        {prop === 'Unique Crops' ? (
          <DealerUniqueCropsField
            value={uniqueCrops}
            setValue={setUniqueCrops}
            states={statesOptions}
            crops={crops} />
        ) : null}
      </div>

      <div className={cls.controls}>
        <BlueButton className={cls.btn} onClick={submit} disabled={loading}>
          Update
        </BlueButton>
        <BlueOutlineButton className={cls.btn} onClick={onCancel}>
          Cancel
        </BlueOutlineButton>
      </div>
    </div>
  );
};
